import { Category } from '@modules/common/types';
import { mapTaxonomyEntityLabelToFeedbackEntityLabel } from '@modules/common/utils';
import { useUpdateEditedPredictions } from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import { trackFeedbackOnFeedbackV2Events } from '@modules/feedback/utils';
import { ManipulationType } from 'generated/graphql';
import { useCallback, useEffect, useState } from 'react';
import { FeedbackLabelType } from 'v2/core/entities/feedback';
import { UUID } from 'v2/core/entities/uuid';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetEntityLabel } from 'v2/views/layouts/utils';
import { shallow } from 'zustand/shallow';

interface UpdateTrackedKeywordCategoryProps {
  feedbackID: UUID;
  labelID: UUID;
  labelName: string;
  category: Category | undefined;
  onUpdateCategory: () => void;
  isDetailView: boolean;
}

export const UpdateTrackedKeywordCategory = ({
  feedbackID,
  labelID,
  labelName,
  category,
  onUpdateCategory,
  isDetailView,
}: UpdateTrackedKeywordCategoryProps) => {
  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const editPredictions = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode?.editPredictions ?? [],
    shallow,
  );
  const [shouldUpdatePrediction, setShouldUpdatePrediction] = useState(false);

  const [status, trackedKeywordLabel] = useGetEntityLabel(labelID);
  const { updateEditedPredictions } = useUpdateEditedPredictions(feedbackID);

  const isLabelLoading = IsLoading(status);

  useEffect(() => {
    if (
      !isLabelLoading &&
      labelID &&
      !shouldUpdatePrediction &&
      trackedKeywordLabel
    ) {
      const prediction =
        mapTaxonomyEntityLabelToFeedbackEntityLabel(trackedKeywordLabel);
      if (prediction.type === FeedbackLabelType.Entity) {
        setShouldUpdatePrediction(true);
        dispatch({
          type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
          payload: {
            id: feedbackID,
            editPredictions: [
              ...editPredictions,
              {
                type: FeedbackLabelType.Entity,
                manipulationType: ManipulationType.Add,
                prediction,
                category,
              },
            ],
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    category,
    isLabelLoading,
    labelID,
    shouldUpdatePrediction,
    trackedKeywordLabel,
  ]);

  const onAfterEditedPredictions = useCallback(() => {
    trackFeedbackOnFeedbackV2Events({
      eventName: 'Prediction Added',
      feedbackID: feedbackID,
      toggleLocation: isDetailView
        ? 'Feedback Detail View'
        : 'Feedback List View',
      predictionType: 'Tracked Keyword',
      predictionName: labelName,
    });
  }, [feedbackID, isDetailView, labelName]);

  useEffect(() => {
    if (shouldUpdatePrediction) {
      setShouldUpdatePrediction(false);
      updateEditedPredictions({
        onAfterEditedPredictions,
      });
      onUpdateCategory();
    }
  }, [
    shouldUpdatePrediction,
    onUpdateCategory,
    updateEditedPredictions,
    onAfterEditedPredictions,
  ]);

  return null;
};
